import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Box, Container, Typography, AppBar, Toolbar, useTheme, CircularProgress, Divider, Grid, Paper, useMediaQuery } from '@mui/material';

// Import components
import LoadingScreen from '../TestChat/components/LoadingScreen';
import EnhancedBackground from '../TestChat/components/EnhancedBackground';
import IntroAnimation from '../TestChat/components/IntroAnimation';
import RobotPipeline from '../TestChat/components/RobotPipeline';
import ChatWidget from '../TestChat/components/ChatWidget';
import FloatingChatButton from '../TestChat/components/FloatingChatButton';

// Import utilities
import { formatResponse } from '../TestChat/utils/formatting';
import { sanitizeHTML } from '../TestChat/utils/sanitizer';

import '../TestChat/styles/animations.css';

const PublicTestChat = () => {
  const theme = useTheme();
  const { id } = useParams();
  const location = useLocation();
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const chatContainerRef = React.useRef(null);
  const [chatOpen, setChatOpen] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAnimation, setShowAnimation] = useState(true);
  const [error, setError] = useState(null);
  
  // Add responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {    
    const fetchBotDetails = async () => {
      try {
        // Use the fully public API endpoint that doesn't require keys
        const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/public/bots/open/${id}`;
        
        const response = await axios.get(url);
        setBot(response.data);
        
        // Add welcome message
        setChatHistory([
          { 
            role: 'bot', 
            content: response.data?.customizations?.welcomeMessage || 'Hello! How can I help you today?' 
          }
        ]);
        
        setLoading(false);
        
        // Hide animation after 5 seconds
        setTimeout(() => setShowAnimation(false), 5000);
      } catch (error) {
        console.error('Error fetching bot details:', error);
        
        // Show appropriate error message based on error code
        if (error.response) {
          if (error.response.status === 404) {
            setError('The bot you requested was not found.');
          } else if (error.response.status === 403) {
            setError('This bot is private and not available for public access.');
          } else {
            setError(error.response?.data?.message || 'Failed to load the bot');
          }
        } else {
          setError('Unable to connect to the server. Please try again later.');
        }
        
        setLoading(false);
      }
    };
    
    fetchBotDetails();
  }, [id]);

  useEffect(() => {
    // Update scroll behavior to show latest messages at the bottom
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleSend = async () => {
    if (!message.trim()) return;
    
    // Add user message to chat
    const userMessage = message;
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setMessage('');
    setSending(true);
    
    try {
      // Create URL for the public chat endpoint (without access key)
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/public/chat/open/${id}`;
      
      // Send message to API
      const response = await axios.post(url, { message: userMessage });
      
      // Add bot response
      setChatHistory(prev => [...prev, { 
        role: 'bot', 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory(prev => [...prev, { 
        role: 'bot', 
        content: 'Sorry, I encountered an error processing your message. Please try again later.' 
      }]);
    } finally {
      setSending(false);
    }
  };

  const toggleChat = () => {
    setChatOpen(!chatOpen);
  };

  const toggleMinimize = () => {
    setMinimized(!minimized);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative', 
        overflow: 'hidden', 
        bgcolor: '#121212',
        p: 3
      }}>
        <Paper elevation={3} sx={{
          p: 4,
          maxWidth: 500,
          textAlign: 'center',
          borderRadius: 2,
          background: 'rgba(40, 40, 45, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'white'
        }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Unable to Access Bot
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">
            Only publicly shared bots are available in this view.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      position: 'relative', 
      overflow: 'hidden', 
      bgcolor: '#121212' 
    }}>
      {/* Enhanced background now covers the entire page with integrated services */}
      <EnhancedBackground bot={bot} />
      
      {/* Intro animation overlay */}
      <IntroAnimation bot={bot} showAnimation={showAnimation} />

      {/* Test Website Header - with higher z-index to appear above the background */}
      <AppBar position="static" sx={{ 
        bgcolor: 'rgba(26, 26, 26, 0.8)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)', 
        zIndex: 10 
      }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {bot?.name || 'AI Assistant'} - Public Demo
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content Area with enhanced welcome section */}
      <Box 
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 }, 
          flexGrow: 1,
          overflowY: 'auto',
          position: 'relative',
          zIndex: 2 // Higher than background but lower than AppBar
        }}
      >
        <Container maxWidth="lg">
          {/* Enhanced Welcome Section */}
          <Paper 
            elevation={3} 
            sx={{
              mt: { xs: 2, sm: 4 },
              mb: { xs: 4, sm: 6 },
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(40, 40, 45, 0.9) 0%, rgba(20, 20, 25, 0.85) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Decorative elements */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: { xs: '120px', sm: '180px', md: '220px' },
                height: { xs: '120px', sm: '180px', md: '220px' },
                background: `radial-gradient(circle, ${bot?.customizations?.primaryColor || '#4C85F0'}30, transparent 70%)`,
                opacity: 0.6,
                filter: 'blur(40px)',
                borderRadius: '50%',
                transform: 'translate(30%, -30%)',
                zIndex: 0
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Typography 
                    variant={isMobile ? "h4" : "h2"} 
                    component="h1" 
                    gutterBottom 
                    sx={{ 
                      color: 'white',
                      fontWeight: 'bold',
                      animation: 'fadeInUp 0.8s',
                      position: 'relative'
                    }}
                  >
                    Welcome to {bot?.name || "Our AI Assistant"}
                  </Typography>
                  
                  <Typography 
                    variant={isMobile ? "body1" : "h5"} 
                    color="rgba(255,255,255,0.8)" 
                    paragraph
                    sx={{ 
                      animation: 'fadeInUp 0.8s 0.2s forwards',
                      opacity: 0,
                      mb: 3
                    }}
                  >
                    This is a public interactive demo of {bot?.name || "this AI chatbot"}.
                    Try asking questions to experience seamless AI conversation.
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      flexWrap: 'wrap',
                      animation: 'fadeInUp 0.8s 0.4s forwards',
                      opacity: 0 
                    }}
                  >
                    {['Natural Language', 'Custom Responses', 'Real-time Chat', 'AI Powered'].map((feature, i) => (
                      <Box 
                        key={feature}
                        sx={{
                          py: 1,
                          px: 2,
                          borderRadius: '20px',
                          bgcolor: 'rgba(255,255,255,0.08)',
                          border: `1px solid ${bot?.customizations?.primaryColor || '#4C85F0'}40`,
                          color: 'white',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Box 
                          sx={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            bgcolor: bot?.customizations?.primaryColor || '#4C85F0',
                            animation: `pulse 1.5s infinite ease-in-out ${i * 0.2}s` 
                          }} 
                        />
                        {feature}
                      </Box>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' }}}>
                  {/* Reusing the animated chat demo from the original TestChat component */}
                  <Box 
                    sx={{ 
                      width: '100%',
                      height: '220px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                      animation: 'fadeIn 1s 0.8s forwards',
                      opacity: 0,
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    {/* Animated AI chat simulation */}
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(180deg, #1e1e2a 0%, #131318 100%)',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Box sx={{ 
                        p: 1, 
                        borderBottom: '1px solid rgba(255,255,255,0.1)', 
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'medium' }}>
                          {bot?.name || "AI Assistant"} Demo
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        overflow: 'hidden'
                      }}>
                        {/* Bot message */}
                        <Box sx={{ 
                          alignSelf: 'flex-start', 
                          maxWidth: '80%',
                          animation: 'fadeInMessage 0.5s forwards'
                        }}>
                          <Box sx={{ 
                            bgcolor: bot?.customizations?.primaryColor || '#4C85F0',
                            color: 'white',
                            p: 1.5,
                            borderRadius: '12px 12px 12px 0',
                          }}>
                            <Typography variant="body2">
                              Hello! How can I assist you today?
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* User message */}
                        <Box sx={{ 
                          alignSelf: 'flex-end', 
                          maxWidth: '80%',
                          opacity: 0,
                          animation: 'fadeInMessage 0.5s forwards 1.2s'
                        }}>
                          <Box sx={{ 
                            bgcolor: 'rgba(255,255,255,0.9)',
                            p: 1.5,
                            borderRadius: '12px 12px 0 12px',
                          }}>
                            <Typography variant="body2">
                              What can you tell me about your services?
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Typing indicator */}
                        <Box sx={{ 
                          alignSelf: 'flex-start',
                          opacity: 0,
                          animation: 'fadeInMessage 0.5s forwards 2.4s'
                        }}>
                          <Box sx={{ 
                            bgcolor: bot?.customizations?.primaryColor || '#4C85F0',
                            p: 1.2,
                            borderRadius: '12px',
                            display: 'flex',
                            gap: 0.5
                          }}>
                            <Box className="typing-dot" />
                            <Box className="typing-dot" />
                            <Box className="typing-dot" />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
          
          {/* Robot Pipeline Section */}
          <Box sx={{ 
            mb: { xs: 5, sm: 7 },
            mt: { xs: 1, sm: 2 },
            position: 'relative',
            zIndex: 2
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h2" 
              gutterBottom 
              sx={{ 
                color: 'white',
                textAlign: 'center',
                fontWeight: 'medium',
                position: 'relative',
                mb: 3,
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60px',
                  height: '3px',
                  backgroundColor: bot?.customizations?.primaryColor || '#4C85F0'
                }
              }}
            >
              How Our AI Works
            </Typography>
            
            <RobotPipeline bot={bot} />
            
            <Divider sx={{ my: { xs: 4, sm: 5 }, bgcolor: 'rgba(255,255,255,0.1)' }} />
          </Box>
          
          {/* Bottom spacing for mobile view so the chat widget doesn't overlap content */}
          <Box sx={{ height: { xs: '120px', sm: '150px' } }} />
        </Container>
      </Box>

      {/* Chat Widget Button - only shown when chat is closed */}
      <FloatingChatButton bot={bot} chatOpen={chatOpen} toggleChat={toggleChat} />

      {/* Floating Chat Widget */}
      <ChatWidget 
        bot={bot}
        chatHistory={chatHistory}
        message={message}
        setMessage={setMessage}
        sending={sending}
        handleSend={handleSend}
        formatResponse={formatResponse}
        chatContainerRef={chatContainerRef}
        minimized={minimized}
        toggleMinimize={toggleMinimize}
        expanded={expanded}
        toggleExpanded={toggleExpanded}
        toggleChat={toggleChat}
        floating={true}
        chatOpen={chatOpen}
      />
    </Box>
  );
};

export default PublicTestChat;
